import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  Stack,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { LogoutOutlined as LogoutIcon, WarningAmberOutlined as WarnIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { forceLogoutAllUsers } from '../../../api/adminSettings.api';

export default function SecurityTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await forceLogoutAllUsers();
      enqueueSnackbar(
        'All users will be logged out on their next request. You will also need to log in again.',
        { variant: 'success' }
      );
      setConfirmOpen(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to force logout. Please try again.';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Card>
        <CardHeader
          title="Force Logout All Users"
          subheader="Emergency switch for ending every active session in this school"
        />
        <CardContent>
          <Stack spacing={2}>
            <Alert severity="warning" icon={<WarnIcon />}>
              Use this only when needed — for example, after a critical security update or
              suspected session compromise. Every user (parents, teachers, students, and you)
              will be sent back to the login screen on their next request.
            </Alert>

            <Typography variant="body2" color="text.secondary">
              Active sessions for this school are valid until they expire or until this switch
              is used. Routine deploys do not need this — the app prompts users to reload when
              a new version is available.
            </Typography>

            <Box>
              <Button
                variant="contained"
                color="error"
                startIcon={<LogoutIcon />}
                onClick={() => setConfirmOpen(true)}
              >
                Force logout all users
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)}>
        <DialogTitle>Force logout every user?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will immediately invalidate all active sessions in your school. Every user,
            including you, will need to log in again. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirm}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <LogoutIcon />}
          >
            {submitting ? 'Logging out…' : 'Yes, log everyone out'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
